import React, { useMemo, useState } from 'react'

import { useDebounce } from 'use-debounce'
import {
  Avatar,
  Box,
  Button,
  FormControl,
  HStack,
  Input,
  InputGroup,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'

import { ATSCandidate } from '@pkg/types/api'

import {
  getATSCandidateFullName,
  getATSCandidatePrimaryEmail,
  hasATSCandidateEmail,
  useSearchATSCandidates,
} from '@/react-query/ats/candidates'
import {
  useCandidate,
  useCreateCandidate,
  useUpdateCandidate,
} from '@/react-query/candidate'

export function InputATSCandidate() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCandidate, setSelectedCandidate] =
    useState<ATSCandidate | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [showResults, setShowResults] = useState(false)

  // Debounce the search query to prevent excessive API calls
  const [debouncedQuery] = useDebounce(searchQuery, 300)

  const createCandidate = useCreateCandidate()
  const updateCandidate = useUpdateCandidate()
  const candidate = useCandidate().data

  const { data: searchResults, isLoading: isSearching } =
    useSearchATSCandidates(debouncedQuery, {
      enabled: debouncedQuery.trim().length > 1,
    })

  // Determine if we should show results based on current input and debounced query
  const shouldShowResults = useMemo(() => {
    return showResults && searchQuery.trim().length > 2
  }, [showResults, searchQuery])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchQuery(value)
    setShowResults(value.trim().length > 2)
    setSelectedCandidate(null)
  }

  const handleCandidateSelect = (atsCandidate: ATSCandidate) => {
    setSelectedCandidate(atsCandidate)
    setSearchQuery(getATSCandidateFullName(atsCandidate))
    setShowResults(false)
  }

  const handleCreateFromATSCandidate = async () => {
    if (!selectedCandidate) return

    setIsCreating(true)
    try {
      const fullName = getATSCandidateFullName(selectedCandidate)

      const candidateData = {
        fullName,
        atsId: selectedCandidate.atsId || undefined,
      }

      if (candidate?.id) {
        await updateCandidate.mutateAsync({
          candidate: {
            id: candidate.id,
            fullName: candidate.fullName || fullName,
            atsId: candidate.atsId || selectedCandidate.atsId || undefined,
          },
        })
      } else {
        await createCandidate.mutateAsync({
          candidate: candidateData,
        })
      }

      // Clear the search after successful creation
      setSearchQuery('')
      setSelectedCandidate(null)
    } catch (error) {
      console.error('Error creating candidate from ATS:', error)
    } finally {
      setIsCreating(false)
    }
  }

  const handleBlur = () => {
    // Delay hiding results to allow for click events
    setTimeout(() => setShowResults(false), 200)
  }

  return (
    <Box position="relative">
      <FormControl>
        <InputGroup
          display="flex"
          flexDirection="row"
          background="white"
          rounded="full"
          position="relative"
        >
          <Input
            placeholder="Search ATS candidates..."
            variant="brand-solid-white"
            rounded="full"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => {
              if (searchQuery.trim().length > 2) {
                setShowResults(true)
              }
            }}
            onBlur={handleBlur}
          />
          {selectedCandidate && (
            <Button
              rounded="1.25rem"
              isLoading={isCreating}
              onClick={handleCreateFromATSCandidate}
              display="flex"
              fontWeight="bold"
              variant="brand-solid-white"
              px={5}
              flexShrink={0}
            >
              {candidate?.id ? 'Update' : 'Create'}
            </Button>
          )}
        </InputGroup>
      </FormControl>

      {/* Search Results Dropdown - styled like MenuList */}
      {shouldShowResults && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={1000}
          mt={2}
          bg="white"
          rounded="1.25rem"
          boxShadow="0px 4px 30px 0px rgba(0, 0, 0, 0.25)"
          py={0}
          maxHeight="300px"
          overflowY="auto"
          overflowX="hidden"
          willChange="transform, opacity"
        >
          {isSearching || debouncedQuery !== searchQuery ? (
            <Box px={4} py={2.5} _first={{ pt: 3.5 }} _last={{ pb: 3.5 }}>
              <Box display="flex" alignItems="center">
                <Spinner size="sm" mr={2} />
                <Text fontWeight="bold">Searching...</Text>
              </Box>
            </Box>
          ) : searchResults && searchResults.length > 0 ? (
            searchResults.map((atsCandidate) => (
              <Box
                key={atsCandidate.id || atsCandidate.atsId}
                px={4}
                py={2.5}
                cursor="pointer"
                bg="white"
                _hover={{ bg: '#f6f6f6' }}
                _first={{ pt: 3.5 }}
                _last={{ pb: 3.5 }}
                onClick={() => handleCandidateSelect(atsCandidate)}
                transition="all 0.2s"
                role="menuitem"
              >
                <HStack spacing={3}>
                  <Avatar
                    size="sm"
                    name={getATSCandidateFullName(atsCandidate)}
                  />
                  <VStack align="start" spacing={0} flex={1}>
                    <Text fontWeight="bold" fontSize="sm">
                      {getATSCandidateFullName(atsCandidate)}
                    </Text>
                    {hasATSCandidateEmail(atsCandidate) && (
                      <Text fontSize="xs" color="gray.600" fontWeight="normal">
                        {getATSCandidatePrimaryEmail(atsCandidate)}
                      </Text>
                    )}
                  </VStack>
                </HStack>
              </Box>
            ))
          ) : debouncedQuery.trim().length > 2 ? (
            <Box
              px={4}
              py={2.5}
              _first={{ pt: 3.5 }}
              _last={{ pb: 3.5 }}
              textAlign="center"
            >
              <Text color="gray.500" fontWeight="bold">
                No candidates found
              </Text>
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  )
}
